import React, { Component } from 'react';
import PropTypes from 'prop-types';
import onClickOutside from 'react-click-outside';
import btnStyles from 'styles/partials/_button.css';
import componentStyles from './_context-button.css';
import TriggerPopover from 'components/connectedForm/TriggerPopover';
import DeprecatedTooltip from 'components/connectedForm/Tooltip';
import ContextButtonContext from '../Context/ContextButtonContext';

export class ContextButton extends Component {
  constructor(props) {
    super(props);

    this.setPopoutPositions = this.setPopoutPositions.bind(this);
    this.addKeyDownHandler = this.addKeyDownHandler.bind(this);
    this.removeKeyDownHandler = this.removeKeyDownHandler.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.keyArrowDown = this.keyArrowDown.bind(this);
    this.keyArrowUp = this.keyArrowUp.bind(this);
    this.keyEnter = this.keyEnter.bind(this);
    this.keyEscape = this.keyEscape.bind(this);
    this.popoutOpen = this.popoutOpen.bind(this);
    this.popoutClose = this.popoutClose.bind(this);

    this.keyDownHandlers = {}; //
    this.keyDownHandlerId = 0;

    this.buttonRef = React.createRef();

    this.state = {
      isOpen: props.initiallyOpen,
    };

    this.styles = {};
    Object.assign(this.styles, componentStyles, btnStyles);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.isOpen && !prevState.isOpen) {
      this.setPopoutPositions();
    }
  }

  // Get width of parent (ContextButton) to use as min-width for this list
  setPopoutPositions() {
    const node = this.buttonRef.current;
    const rect = node.getBoundingClientRect();
    let width = rect.width > this.props.maxWidth ? rect.width : this.props.maxWidth;
    let alignLeft = rect.left + width > window.innerWidth;
    if (this.props.dropdownClass === 'filterDropdown') width = 450;
    this.setState({
      dropdownTop: rect.bottom,
      dropdownLeft: rect.left,
      dropdownWidth: width,
      alignLeft,
    });
  }

  addKeyDownHandler(handler) {
    let handlerId = this.keyDownHandlerId++;
    this.keyDownHandlers[handlerId] = handler;
    return handlerId;
  }

  removeKeyDownHandler(handlerId) {
    delete this.keyDownHandlers[handlerId];
  }

  handleClick() {
    this.popoutOpen();
  }

  handleClickOutside(ev) {
    if (!this.props.handleClickOutside(ev)) {
      return;
    }
    this.popoutClose();
  }

  handleKeyDown(event) {
    // Process any key down events handled by this control
    if (this['key' + event.key]) {
      this['key' + event.key].call(this, event);
    }

    Object.keys(this.keyDownHandlers).forEach((handlerId) => {
      this.keyDownHandlers[handlerId](event);
    });
  }

  // Down arrow key event handler
  keyArrowDown(event) {
    this.popoutOpen();
  }

  // Up arrow key event handler
  keyArrowUp(event) {
    this.popoutOpen();
  }

  // Enter key opens the content (if already open does nothing)
  keyEnter(event) {
    this.popoutOpen();
  }

  // Escape key closes content
  keyEscape(event) {
    this.popoutClose();
  }

  popoutOpen() {
    if (this.state.isOpen === false) {
      this.setState({ isOpen: true });
    }
  }

  popoutClose() {
    if (this.state.isOpen === true) {
      this.setState({ isOpen: false });
      this.props.onClose();
    }
  }

  render() {
    let popout;
    let style = null;
    if (this.state.isOpen) {
      style =
        this.props.alignLeft || this.state.alignLeft
          ? {
              right: 0,
              top: '100%',
              minWidth: this.state.dropdownWidth,
            }
          : {
              left: 0,
              top: '100%',
              minWidth: this.state.dropdownWidth,
            };
    } else {
      style = {
        display: 'none',
      };
    }
    popout = (
      <div className={this.styles[this.props.dropdownClass]} style={style}>
        {this.props.children}
      </div>
    );
    let x = '';
    let buttonPaddingClass = 'btnWithDropdown';

    if (this.props.dismissFilter) {
      x = (
        <span onClick={this.props.dismissFilter} data-testid={this.props.testid}>
          <i className="iconSmall">{'clear'}</i>
        </span>
      );
    }

    if (this.props.labelIcon || this.props.labelImage) {
      buttonPaddingClass = 'btnWithIcon';
    }

    let optionIcon = null;
    if (this.props.optionIcon) {
      if (this.props.optionTooltip) {
        buttonPaddingClass = 'btnWithAddon';
        optionIcon = (
          <div
            onClick={(e) => {
              e.stopPropagation();
              this.props.onOptionIconClick();
            }}
            className={this.styles[this.props.buttonClass] + ' ' + this.styles['btnAddon']}
          >
            <TriggerPopover
              popover={<DeprecatedTooltip value={this.props.optionTooltip} />}
              flexBox={false}
              buttonAddon={true}
            >
              <i className="btnIcon">{this.props.optionIcon}</i>
            </TriggerPopover>
          </div>
        );
      } else {
        buttonPaddingClass = 'btnWithAddon';
        optionIcon = (
          <div
            onClick={(e) => {
              e.stopPropagation();
              this.props.onOptionIconClick();
            }}
            className={this.styles[this.props.buttonClass] + ' ' + this.styles['btnAddon']}
          >
            <i className="icon">{this.props.optionIcon}</i>
          </div>
        );
      }
    }
    const label = typeof this.props.label === 'string' ? this.props.label : 'contextButton';
    const testid = this.props.dataTestId + label.replace(' ', '_');

    return (
      <ContextButtonContext.Provider
        value={{
          addKeyDownHandler: this.addKeyDownHandler,
          removeKeyDownHandler: this.removeKeyDownHandler,
        }}
      >
        <div style={this.props.style} className={this.styles[this.props.containerClass]}>
          <div className={this.styles[this.props.buttonOuterClass]} onKeyDown={this.handleKeyDown}>
            <button
              className={this.styles[this.props.buttonClass] + ' ' + this.styles[buttonPaddingClass]}
              type="button"
              ref={this.buttonRef}
              onClick={this.handleClick}
              tabIndex={this.props.tabIndex}
              data-testid={testid}
              style={this.props.style}
            >
              <div className={this.styles[this.props.buttonLabelClass]} aria-hidden="true">
                {this.props.labelIcon && (
                  <span className={this.styles['btnIcon']} aria-hidden="true">
                    {this.props.labelIcon}
                  </span>
                )}
                {this.props.labelImage && (
                  <span className={this.styles['btnIcon']} aria-hidden="true">
                    <img src={this.props.labelImage} />
                  </span>
                )}
                <span className={this.styles['btnLabelFilterText']}>{this.props.label}</span>
                {this.props.includeCaret && (
                  <div className={this.styles['btnControl']}>
                    <span className={this.styles['btnCaret']} aria-hidden="true" />
                    {x}
                  </div>
                )}
              </div>
            </button>
            {optionIcon}
            {popout}
          </div>
        </div>
      </ContextButtonContext.Provider>
    );
  }
}

ContextButton.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  containerClass: PropTypes.string,
  dropdownClass: PropTypes.string,
  buttonOuterClass: PropTypes.string,
  buttonClass: PropTypes.string,
  buttonLabelClass: PropTypes.string,
  tabIndex: PropTypes.number,
  handleClickOutside: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  initiallyOpen: PropTypes.bool,
  optionIcon: PropTypes.string,
  onOptionIconClick: PropTypes.func,
  style: PropTypes.object,
  maxWidth: PropTypes.number,
  includeCaret: PropTypes.bool,
  dataTestId: PropTypes.string,
};

ContextButton.defaultProps = {
  handleClickOutside() {
    return true;
  },
  onClose() {},
  initiallyOpen: false,
  containerClass: 'container',
  dropdownClass: 'dropdown',
  buttonOuterClass: 'btnContainer',
  buttonClass: 'btnDefault',
  buttonLabelClass: 'btnLabel',
  labelIcon: null,
  alignLeft: false,
  style: null,
  maxWidth: 300,
  includeCaret: true,
  dataTestId: 'contextButton_',
};

let ClickOutsideContextButton = onClickOutside(ContextButton);

class EnhancedContextButton extends React.Component {
  constructor(props) {
    super(props);
    this.getMyComponentRef = this.getMyComponentRef.bind(this);
    this.popoutClose = this.popoutClose.bind(this);
  }

  popoutClose = () => {
    this.wrappedComponent.__wrappedInstance.popoutClose();
  };

  getMyComponentRef(ref) {
    this.wrappedComponent = ref;
  }

  render() {
    return <ClickOutsideContextButton {...this.props} ref={this.getMyComponentRef} />;
  }
}

export default EnhancedContextButton;
